/**
 * timezone.utils.ts
 * Utility functions for handling timezone conversions
 * 
 * Database stores: UTC timestamps (e.g., "2026-01-28 16:00:00")
 * Display shows: User's local timezone (e.g., 17:00 in Nigeria, 16:00 in UK, 11:00 in US/EST)
 */

import { format } from 'date-fns';

/**
 * Convert UTC datetime string from database to user's local timezone Date object
 * JavaScript Date automatically converts to browser's timezone
 * 
 * @param utcDateTimeString - UTC datetime from database (e.g., "2026-01-28 16:00:00")
 * @returns Date object in user's local timezone
 */
export function convertUTCToLocal(utcDateTimeString: string): Date {
  if (!utcDateTimeString) {
    throw new Error('Invalid datetime string');
  }

  // SQL Server datetime format: "2026-01-28 16:00:00.0000000"
  // We need to convert it to ISO format for JavaScript
  
  // Replace space with 'T' and add 'Z' to indicate UTC
  // "2026-01-28 16:00:00" → "2026-01-28T16:00:00Z"
  const isoString = utcDateTimeString.replace(' ', 'T').split('.')[0] + 'Z';
  
  // Create Date object - browser automatically converts to local timezone
  const localDate = new Date(isoString);
  
  return localDate;
}

/**
 * Format UTC datetime for display in user's local timezone
 * 
 * @param utcDateTimeString - UTC datetime from database
 * @param formatString - date-fns format string (optional)
 * @returns Formatted string in user's local timezone
 */
export function formatUTCToLocal(
  utcDateTimeString: string,
  formatString: string = "EEEE, MMMM do yyyy 'at' h:mm a"
): string {
  try {
    const localDate = convertUTCToLocal(utcDateTimeString);
    return format(localDate, formatString);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
}

/**
 * Format with timezone abbreviation
 * Example: "Sunday, January 28th 2026 at 5:00 PM WAT"
 */
export function formatUTCToLocalWithTimezone(utcDateTimeString: string): string {
  try {
    const localDate = convertUTCToLocal(utcDateTimeString);
    
    // Get timezone abbreviation
    const tzAbbr = getTimezoneAbbreviation();
    
    // Format with date-fns
    const formatted = format(localDate, "EEEE, MMMM do yyyy 'at' h:mm a");
    
    return `${formatted} ${tzAbbr}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
}

/**
 * Get user's timezone abbreviation (WAT, GMT, EST, etc.)
 */
export function getTimezoneAbbreviation(): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZoneName: 'short'
    });
    const parts = formatter.formatToParts(new Date());
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');
    return timeZonePart?.value || 'Local Time';
  } catch (error) {
    console.error('Error getting timezone:', error);
    return 'Local Time';
  }
}

/**
 * Get user's full timezone name
 * Example: "Africa/Lagos", "Europe/London", "America/New_York"
 */
export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Format short date (no time)
 * Example: "Jan 28, 2026"
 */
export function formatDateShort(utcDateTimeString: string): string {
  try {
    const localDate = convertUTCToLocal(utcDateTimeString);
    return format(localDate, "MMM d, yyyy");
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
}

/**
 * Format time only
 * Example: "5:00 PM"
 */
export function formatTimeOnly(utcDateTimeString: string): string {
  try {
    const localDate = convertUTCToLocal(utcDateTimeString);
    return format(localDate, "h:mm a");
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid time';
  }
}

/**
 * Format date and time separately
 * Returns: { date: "Jan 28, 2026", time: "5:00 PM", timezone: "WAT" }
 */
export function formatDateTimeSeparate(utcDateTimeString: string): {
  date: string;
  time: string;
  timezone: string;
} {
  try {
    const localDate = convertUTCToLocal(utcDateTimeString);
    return {
      date: format(localDate, "MMM d, yyyy"),
      time: format(localDate, "h:mm a"),
      timezone: getTimezoneAbbreviation()
    };
  } catch (error) {
    console.error('Error formatting date/time:', error);
    return {
      date: 'Invalid date',
      time: 'Invalid time',
      timezone: ''
    };
  }
}

/**
 * Check if event is in the past
 */
export function isEventPast(utcDateTimeString: string): boolean {
  try {
    const localDate = convertUTCToLocal(utcDateTimeString);
    return localDate < new Date();
  } catch (error) {
    return false;
  }
}

/**
 * Check if event is today
 */
export function isEventToday(utcDateTimeString: string): boolean {
  try {
    const localDate = convertUTCToLocal(utcDateTimeString);
    const today = new Date();
    
    return localDate.getDate() === today.getDate() &&
           localDate.getMonth() === today.getMonth() &&
           localDate.getFullYear() === today.getFullYear();
  } catch (error) {
    return false;
  }
}

/**
 * Get relative time (e.g., "in 2 hours", "yesterday")
 * Requires date-fns formatDistanceToNow
 */
export function getRelativeTime(utcDateTimeString: string): string {
  try {
    const localDate = convertUTCToLocal(utcDateTimeString);
    const now = new Date();
    
    // Simple implementation
    const diffMs = localDate.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return `${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24 && diffHours >= 0) {
      return `in ${diffHours} hour${diffHours !== 1 ? 's' : ''}`;
    } else {
      return `in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    }
  } catch (error) {
    return '';
  }
}